;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();const FINANCIALS_GROUP_LABEL = "Financials";

const FINANCIAL_VIEW_CONFIG = {
	"accounts payable": {
		label: "Accounts Payable",
		order: 0,
	},
	"accounts receivable": {
		label: "Accounts Receivable",
		order: 1,
	},
	"financial performance": {
		label: "Performance",
		order: 2,
	},
	"financial statements": {
		label: "Statements",
		order: 3,
	},
	loans: {
		label: "Loans",
		order: 4,
	},
} as const;
type FinancialViewKey = keyof typeof FINANCIAL_VIEW_CONFIG;

export interface WorkspaceMenuView {
	id: string;
	name: string;
}

export interface WorkspaceMenuLinkItem<
	TView extends WorkspaceMenuView = WorkspaceMenuView,
> {
	kind: "link";
	isActive: boolean;
	label: string;
	view: TView;
}

export interface WorkspaceMenuGroupItem<
	TView extends WorkspaceMenuView = WorkspaceMenuView,
> {
	kind: "group";
	id: "financials";
	isExpanded: boolean;
	isActive: boolean;
	items: WorkspaceMenuLinkItem<TView>[];
	label: typeof FINANCIALS_GROUP_LABEL;
}

export type WorkspaceMenuItem<
	TView extends WorkspaceMenuView = WorkspaceMenuView,
> = WorkspaceMenuGroupItem<TView> | WorkspaceMenuLinkItem<TView>;

interface BuildWorkspaceMenuItemsOptions {
	sort?: boolean;
}

function normalizeViewName(viewName: string) {
	return viewName.trim().toLowerCase();
}

function isFinancialViewKey(viewName: string): viewName is FinancialViewKey {
	return viewName in FINANCIAL_VIEW_CONFIG;
}

function isFinancialView(viewName: string) {
	return isFinancialViewKey(normalizeViewName(viewName));
}

function getSortOrder(viewName: string) {
	const normalizedViewName = normalizeViewName(viewName);

	if (normalizedViewName === "leaderboard") return 0;
	if (normalizedViewName === "communications") return 2;
	return 1;
}

export function buildWorkspaceMenuItems<TView extends WorkspaceMenuView>(
	views: TView[],
	currentView: string,
	options: BuildWorkspaceMenuItemsOptions = {},
): WorkspaceMenuItem<TView>[] {
	const normalizedCurrentView = normalizeViewName(currentView);
	const { sort = true } = options;
	const indexedViews = views.map((view, index) => ({ index, view }));
	const sortedViews = sort
		? indexedViews.toSorted((viewA, viewB) => {
				const orderDifference =
					getSortOrder(viewA.view.name) - getSortOrder(viewB.view.name);

				if (orderDifference !== 0) {
					return orderDifference;
				}

				return viewA.index - viewB.index;
			})
		: indexedViews;

	const financialViewsByName = new Map(
		sortedViews
			.filter(({ view }) => isFinancialView(view.name))
			.map(({ view }) => [normalizeViewName(view.name), view]),
	);

	const financialGroupItems = Object.entries(FINANCIAL_VIEW_CONFIG)
		.map(([viewName, config]) => {
			const matchedView = financialViewsByName.get(viewName);

			if (!matchedView) {
				return null;
			}

			return {
				isActive: normalizeViewName(matchedView.name) === normalizedCurrentView,
				kind: "link" as const,
				label: config.label,
				view: matchedView,
			};
		})
		.filter((item) => item !== null)
		.toSorted((itemA, itemB) => {
			const itemAName = normalizeViewName(itemA.view.name);
			const itemBName = normalizeViewName(itemB.view.name);
			if (!isFinancialViewKey(itemAName) || !isFinancialViewKey(itemBName)) {
				return 0;
			}

			return (
				FINANCIAL_VIEW_CONFIG[itemAName].order -
				FINANCIAL_VIEW_CONFIG[itemBName].order
			);
		});

	const hasFinancialViews = financialGroupItems.length > 0;
	let hasInsertedFinancialGroup = false;

	return sortedViews.reduce<WorkspaceMenuItem<TView>[]>(
		(menuItems, { view }) => {
			if (isFinancialView(view.name)) {
				if (!hasInsertedFinancialGroup && hasFinancialViews) {
					hasInsertedFinancialGroup = true;
					menuItems.push({
						id: "financials",
						isActive: financialGroupItems.some((item) => item.isActive),
						isExpanded: financialGroupItems.some((item) => item.isActive),
						items: financialGroupItems,
						kind: "group",
						label: FINANCIALS_GROUP_LABEL,
					});
				}

				return menuItems;
			}

			menuItems.push({
				isActive: normalizeViewName(view.name) === normalizedCurrentView,
				kind: "link",
				label: view.name,
				view,
			});

			return menuItems;
		},
		[],
	);
}
